/* assets/image-scroll-scrub.js
 * Scroll-scrub image sequence controller.
 * Uses two stacked <img> layers and cross-fades between frames so the change isn't visible.
 *
 * Markup expected:
 *  - Wrapper with [data-scroll-scrub-wrap] controlling scroll distance
 *  - Section root with [data-scroll-scrub-section] and optional data-scrub-smoothing="0.15"
 *  - Sequence element with [data-image-scroll-scrub]
 *      data-frames-desktop='["url1","url2",...]'
 *      data-frames-mobile='["url1","url2",...]' (optional)
 */

(() => {
  const MOBILE_MAX_WIDTH = 749;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  function getViewportHeight() {
    // Prefer visualViewport for mobile browsers (prevents bottom gaps when the URL bar changes)
    return (window.visualViewport && window.visualViewport.height)
      ? window.visualViewport.height
      : window.innerHeight;
  }

  function isMobileViewport() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
  }

  function getScrubProgress(wrapEl) {
    const rect = wrapEl.getBoundingClientRect();
    const total = rect.height - getViewportHeight();
    if (total <= 0) return rect.top < 0 ? 1 : 0;
    return clamp((-rect.top) / total, 0, 1);
  }

  function safeParseJson(attrVal) {
    if (!attrVal) return [];
    try {
      const parsed = JSON.parse(attrVal);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function preload(urls) {
    // Best-effort preload. Browser will de-dupe.
    urls.forEach((u) => {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = u;
    });
  }

  // Decode-aware cache to eliminate flicker/glitches when scrubbing fast (especially upward).
  // We progressively decode frames in idle time so swaps are instant.
  // Cache also stores the Image instance so we can draw to canvas (mobile fallback) without extra requests.
  const _frameCache = new Map(); // url -> { promise: Promise<void>, img: HTMLImageElement }

  function preloadAndDecode(url) {
    if (!url) return Promise.resolve();
    if (_frameCache.has(url)) return _frameCache.get(url).promise;

    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;

    const p = (img.decode
      ? img.decode().catch(() => {})
      : new Promise((resolve) => {
        // Older Safari sometimes lacks decode(). Use load/error as best-effort.
        img.onload = () => resolve();
        img.onerror = () => resolve();
      })
    );

    _frameCache.set(url, { promise: p, img });
    return p;
  }

  function getCachedImage(url) {
    const cached = _frameCache.get(url);
    return cached?.img || null;
  }

  function idleDecodeQueue(urls) {
    const list = (urls || []).filter(Boolean);
    let i = 0;

    const runSlice = (deadline) => {
      // Decode a handful per idle slice.
      let count = 0;
      while (i < list.length && (deadline?.timeRemaining?.() > 6 || count < 2)) {
        preloadAndDecode(list[i]);
        i += 1;
        count += 1;
      }
      if (i < list.length) {
        schedule();
      }
    };

    const schedule = () => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(runSlice, { timeout: 250 });
      } else {
        setTimeout(() => runSlice({ timeRemaining: () => 10 }), 30);
      }
    };

    schedule();
  }

  function setSrcWhenReady(imgEl, url, token, tokenRef) {
    // Wait for the cached decode before swapping src to avoid showing half-loaded frames.
    // token/tokenRef guard against out-of-order async resolves.
    return preloadAndDecode(url).then(() => {
      if (tokenRef.value !== token) return;
      if (imgEl.src !== url) imgEl.src = url;
      // Ensure the element has decoded its current src (best-effort).
      if (imgEl.decode) {
        return imgEl.decode().catch(() => {});
      }
    });
  }

  function initOne(seqEl) {
    const wrap = seqEl.closest('[data-scroll-scrub-wrap]');
    if (!wrap) return;

    const section = seqEl.closest('[data-scroll-scrub-section]') || wrap.closest('section');

    // Keep a stable viewport height var on the wrap so CSS can perfectly fill the screen on mobile.
    function updateVhVar() {
      const vh = Math.max(1, Math.round(getViewportHeight()));
      wrap.style.setProperty('--scrub-vh', `${vh}px`);
    }
    updateVhVar();
    const smoothingAttr = section?.getAttribute('data-scrub-smoothing');
    const smoothing = clamp(parseFloat(smoothingAttr || '0.15'), 0.02, 0.5);

    // Narrative extras (timed cues, mobile panel, progress bar)
    const cueEls = Array.from(section?.querySelectorAll('[data-scrub-cue]') || []);
    const progressBar = section?.querySelector('[data-scrub-progress]') || null;
    const mobileHeadingEl = section?.querySelector('[data-scrub-mobile-heading]') || null;
    const mobileTextEl = section?.querySelector('[data-scrub-mobile-text]') || null;

    // Gate the mobile text panel until we have a first successful paint (avoids scrolling text while frames are blank).
    let firstPaintDone = false;
    let loadingApplied = false;
    function setLoading(on) {
      if (!section) return;
      if (on) {
        section.classList.add('is-scrub-loading');
        if (isMobileViewport() && (mobileHeadingEl || mobileTextEl) && !loadingApplied) {
          if (mobileHeadingEl) mobileHeadingEl.textContent = 'Loading...';
          if (mobileTextEl) mobileTextEl.textContent = '';
          loadingApplied = true;
        }
      } else {
        section.classList.remove('is-scrub-loading');
      }
    }

    const desktopFrames = safeParseJson(seqEl.getAttribute('data-frames-desktop'));
    const mobileFrames = safeParseJson(seqEl.getAttribute('data-frames-mobile'));
    const pickFrames = () => {
      const useMobile = isMobileViewport();
      if (useMobile && mobileFrames.length) return mobileFrames;
      return desktopFrames;
    };

    let frames = pickFrames();
    if (!frames.length) return;

    // Start decoding the first frames immediately (helps transparent PNG sequences on iOS).
    preloadAndDecode(frames[0]);
    preloadAndDecode(frames[Math.min(1, frames.length - 1)]);

    // Preload + progressively decode (prevents flicker on fast scrubs)
    // 1) Eagerly preload the first few frames immediately
    preload(desktopFrames.slice(0, 6));
    if (mobileFrames.length) preload(mobileFrames.slice(0, 6));
    // 2) Decode the full sequences in idle time
    idleDecodeQueue(desktopFrames);
    if (mobileFrames.length) idleDecodeQueue(mobileFrames);

    const imgA = seqEl.querySelector('[data-scrub-img-a]');
    const imgB = seqEl.querySelector('[data-scrub-img-b]');
    if (!imgA || !imgB) return;

    // Optional mobile-only canvas renderer (enabled per-section via data-mobile-render="canvas")
    const mobileRenderMode = (seqEl.getAttribute('data-mobile-render') || '').toLowerCase();
    let canvas = null;
    let ctx = null;
    let canvasActive = false;

    function ensureCanvas() {
      if (canvas && ctx) return;
      canvas = document.createElement('canvas');
      canvas.className = 'image-scrub__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      // Match the image layers' positioning (absolute inset 0).
      canvas.style.position = 'absolute';
      canvas.style.inset = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      seqEl.appendChild(canvas);
      ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });
    }

    function resizeCanvasToDisplaySize() {
      if (!canvas) return;
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        if (ctx) ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
    }

    function activateCanvas() {
      ensureCanvas();
      resizeCanvasToDisplaySize();
      imgA.style.display = 'none';
      imgB.style.display = 'none';
      canvasActive = true;

      // Ensure the first frame is decoded as soon as possible.
      setLoading(true);
      preloadAndDecode(frames[0]).then(() => requestUpdate());
      if (frames.length > 1) preloadAndDecode(frames[1]);
    }

    function deactivateCanvas() {
      imgA.style.display = '';
      imgB.style.display = '';
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = null;
      ctx = null;
      canvasActive = false;
    }

    function drawContain(c, img, alpha) {
      if (!c || !canvas || !img) return false;
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      if (!iw || !ih) return false;

      const cw = canvas.width;
      const ch = canvas.height;
      const scale = Math.min(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (cw - dw) * 0.5;
      const dy = (ch - dh) * 0.5;

      c.save();
      c.globalAlpha = typeof alpha === 'number' ? alpha : 1;
      c.drawImage(img, dx, dy, dw, dh);
      c.restore();
      return true;
    }

    let rafId = null;
    let lastP = 0; // rendered (smoothed) progress
    let lastRawP = 0; // last raw progress
    let lastRawDir = 0; // last raw direction
    let lastIdx0 = -1;
    let lastIdx1 = -1;
    let tokenA = 0;
    let tokenB = 0;
    const tokenRefA = { value: 0 };
    const tokenRefB = { value: 0 };

    function setFrame(idx0, idx1, frac) {
      if (!frames.length) return;

      idx0 = clamp(idx0, 0, frames.length - 1);
      idx1 = clamp(idx1, 0, frames.length - 1);

      const url0 = frames[idx0];
      const url1 = frames[idx1];

      // Decode a small neighborhood around the current indices to keep reversals perfect.
      for (let k = -2; k <= 2; k += 1) {
        const n0 = idx0 + k;
        const n1 = idx1 + k;
        if (n0 >= 0 && n0 < frames.length) preloadAndDecode(frames[n0]);
        if (n1 >= 0 && n1 < frames.length) preloadAndDecode(frames[n1]);
      }

      if (canvasActive && ctx) {
        // Canvas renderer (mobile only, per-section): prevents iOS flicker during sticky scroll.
        resizeCanvasToDisplaySize();

        const img0 = getCachedImage(url0);
        const img1 = getCachedImage(url1);

        // If base frame isn't ready yet, keep the previous canvas content and re-render when decoded.
        const baseReady = !!(img0 && (img0.naturalWidth || img0.width));
        if (!baseReady) {
          preloadAndDecode(url0).then(() => requestUpdate());
          return;
        }

        // Draw base (and optionally blend next) with object-fit: contain behavior.
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawContain(ctx, img0, 1);

        if (!firstPaintDone) {
          firstPaintDone = true;
          setLoading(false);
        }

        const f = clamp(frac, 0, 1);
        if (idx1 !== idx0 && f > 0.001 && img1) {
          const nextReady = !!(img1.naturalWidth || img1.width);
          if (nextReady) {
            drawContain(ctx, img1, f);
          } else {
            preloadAndDecode(url1).then(() => requestUpdate());
          }
        }

        lastIdx0 = idx0;
        lastIdx1 = idx1;
        return;
      }

      // Default renderer (two <img> layers)
      if (idx0 !== lastIdx0) {
        tokenA += 1;
        tokenRefA.value = tokenA;
        setSrcWhenReady(imgA, url0, tokenA, tokenRefA);
        lastIdx0 = idx0;
      }
      if (idx1 !== lastIdx1) {
        tokenB += 1;
        tokenRefB.value = tokenB;
        setSrcWhenReady(imgB, url1, tokenB, tokenRefB);
        lastIdx1 = idx1;
      }

      // Cross-fade next frame over current frame.
      imgB.style.opacity = `${clamp(frac, 0, 1)}`;
    }

    function requestUpdate() {
      if (rafId) return;
      rafId = requestAnimationFrame(update);
    }

    function update() {
      rafId = null;

      // Toggle renderer based on viewport (canvas is mobile-only and opt-in per section)
      const wantCanvas = (mobileRenderMode === 'canvas') && isMobileViewport();
      if (wantCanvas && !canvasActive) activateCanvas();
      if (!wantCanvas && canvasActive) deactivateCanvas();

      if (!wantCanvas) {
        // If we're not in canvas mode, ensure any loading state is cleared.
        firstPaintDone = true;
        setLoading(false);
      } else if (!firstPaintDone) {
        setLoading(true);
      }

      // Re-pick frames on viewport changes (e.g. rotate)
      const newFrames = pickFrames();
      if (newFrames !== frames) {
        frames = newFrames;
        // New sources (e.g. rotate) may need a new first paint.
        firstPaintDone = false;
        setLoading(true);
        preloadAndDecode(frames[0]);
        preloadAndDecode(frames[Math.min(1, frames.length - 1)]);
      }

      const p = getScrubProgress(wrap);

      // If the user reverses scroll direction, snapping prevents a brief "hiccup"
      // caused by smoothing lagging behind the new direction.
      // Direction detection with hysteresis to avoid tiny scroll jitter flipping direction.
// Small oscillations can happen during momentum scrolling, especially on touchpads.
const delta = p - lastRawP;
const DIR_EPS = 0.0015;
const dirNow = delta > DIR_EPS ? 1 : (delta < -DIR_EPS ? -1 : 0);

// If the user truly reverses scroll direction, snapping prevents a brief "hiccup"
// caused by smoothing lagging behind the new direction.
// Only snap on a meaningful reversal (bigger than DIR_EPS) so we don't snap on noise.
if (dirNow !== 0 && lastRawDir !== 0 && dirNow !== lastRawDir) {
  lastP = p;
}

if (dirNow !== 0) lastRawDir = dirNow;
lastRawP = p;

      // Update progress bar
      if (progressBar) {
        progressBar.style.width = `${(p * 100).toFixed(4)}%`;
      }

      // Update timed cues (desktop/tablet overlay)
      if (cueEls.length) {
        for (const cue of cueEls) {
          const start = parseFloat(cue.getAttribute('data-start') || '0');
          const end = parseFloat(cue.getAttribute('data-end') || '0');
          const active = p >= start && p <= end;
          cue.style.opacity = active ? '1' : '0';
          cue.style.visibility = active ? 'visible' : 'hidden';
          cue.style.pointerEvents = active ? 'auto' : 'none';
        }
      }

      // Update mobile fixed panel by choosing the most recent active cue
      if (isMobileViewport() && (mobileHeadingEl || mobileTextEl) && cueEls.length && firstPaintDone) {
        let chosen = null;
        let chosenStart = -1;
        for (const cue of cueEls) {
          const start = parseFloat(cue.getAttribute('data-start') || '0');
          const end = parseFloat(cue.getAttribute('data-end') || '0');
          if (p >= start && p <= end && start >= chosenStart) {
            chosen = cue;
            chosenStart = start;
          }
        }

        if (chosen) {
          const h = chosen.querySelector('.scroll-scrub-cue__heading');
          const t = chosen.querySelector('.scroll-scrub-cue__text');

          const mh = chosen.getAttribute('data-mobile-heading');
          const mt = chosen.getAttribute('data-mobile-text');
          const mhSize = parseFloat(chosen.getAttribute('data-mobile-heading-size') || '');
          const mtSize = parseFloat(chosen.getAttribute('data-mobile-text-size') || '');

          if (mobileHeadingEl) {
            mobileHeadingEl.innerHTML = (mh && mh.trim().length) ? mh : (h ? h.innerHTML : '');
            if (!Number.isNaN(mhSize)) {
              mobileHeadingEl.style.setProperty('--adjust-heading', `calc(var(--FONT-ADJUST-HEADING) * ${mhSize})`);
            } else {
              mobileHeadingEl.style.removeProperty('--adjust-heading');
            }
          }

          if (mobileTextEl) {
            mobileTextEl.innerHTML = (mt && mt.trim().length) ? mt : (t ? t.innerHTML : '');
            if (!Number.isNaN(mtSize)) {
              mobileTextEl.style.setProperty('--adjust-body', `calc(var(--FONT-ADJUST-BODY) * ${mtSize})`);
            } else {
              mobileTextEl.style.removeProperty('--adjust-body');
            }
          }
        } else {
          if (mobileHeadingEl) {
            mobileHeadingEl.innerHTML = '';
            mobileHeadingEl.style.removeProperty('--adjust-heading');
          }
          if (mobileTextEl) {
            mobileTextEl.innerHTML = '';
            mobileTextEl.style.removeProperty('--adjust-body');
          }
        }
      }

      // Smooth frame progress to avoid jitter from scroll events.
// We animate toward the latest raw progress (p).
// On fast reverse scroll (especially upward), heavy smoothing can cause a visible mismatch,
// so we use adaptive smoothing and a max-lag snap.
const diffToTarget = p - lastP;
const absDiffToTarget = Math.abs(diffToTarget);

// If we're too far behind the user's scroll position, snap to avoid visible "glitches".
// Upward scrubs tend to reveal lag more, so we allow less lag when scrolling up.
const LAG_SNAP_DOWN = 0.08;
const LAG_SNAP_UP = 0.03;
const lagSnap = (dirNow === -1 ? LAG_SNAP_UP : LAG_SNAP_DOWN);

if (absDiffToTarget >= lagSnap) {
  lastP = p;
} else {
  // Adaptive smoothing: the further we are from the target, the quicker we catch up.
  // This preserves smoothness for small deltas but stays responsive on quick scrubs.
  const boost = clamp(absDiffToTarget * 6, 0, 1); // 0..1
  const dirBoost = (dirNow === -1 ? 0.25 : 0.10);
  const smoothingEff = clamp(smoothing + boost * 0.45 + dirBoost, 0.02, 0.9);
  lastP = lastP + diffToTarget * smoothingEff;
}

      const maxIdx = Math.max(frames.length - 1, 0);
      const frameF = lastP * maxIdx;
      const idx0 = Math.floor(frameF);
      const idx1 = Math.min(idx0 + 1, maxIdx);
      const frac = frameF - idx0;

      setFrame(idx0, idx1, frac);

      // Continue animating only while we're still catching up to the scroll position.
      if (Math.abs(p - lastP) > 0.0005) {
        requestUpdate();
      }
    }

    // Initialize first frame immediately.
    imgA.decoding = 'async';
    imgB.decoding = 'async';
    imgA.loading = 'eager';
    imgB.loading = 'eager';

    // Initialize first frames (decode-aware so first reverse scrub is clean)
    const first0 = frames[0];
    const first1 = frames[Math.min(1, frames.length - 1)];
    tokenA += 1;
    tokenRefA.value = tokenA;
    setSrcWhenReady(imgA, first0, tokenA, tokenRefA);
    tokenB += 1;
    tokenRefB.value = tokenB;
    setSrcWhenReady(imgB, first1, tokenB, tokenRefB);
    imgB.style.opacity = '0';

    // Run updates only when the section is near viewport to save work.
    const io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        requestUpdate();
      } else {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      }
    }, { root: null, threshold: 0 });

    io.observe(wrap);

    window.addEventListener('resize', () => { updateVhVar(); requestUpdate(); }, { passive: true });
    window.addEventListener('scroll', requestUpdate, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => { updateVhVar(); requestUpdate(); }, { passive: true });
      window.visualViewport.addEventListener('scroll', () => { updateVhVar(); requestUpdate(); }, { passive: true });
    }

    requestUpdate();
  }

  function init() {
    document.querySelectorAll('[data-image-scroll-scrub]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();